/* Adapter owns only navigation state; never rewrites cart, accounts or forms. */
(() => {
  if (customElements.get('ew-navigation')) return;
  class EWNavigation extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.header = this.closest('.header');
      this.nav = this.querySelector('[data-ew-items]');
      this.toggleButton = [...(this.header?.children || [])].find(child => (
        child.matches?.('[data-ew-toggle]')
        && child.getAttribute('aria-controls') === this.nav?.id
      ));
      if (!this.header || !this.toggleButton || !this.nav) return;
      this.toggleButton.hidden = false;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.menus = [...this.querySelectorAll('[data-ew-menu]')];
      this.section = this.closest('.section-header');
      this.stickyHeader = this.closest('sticky-header');
      this.logo = this.header.querySelector(':scope > .header__heading, :scope > .header__heading-link');
      this.icons = this.header.querySelector(':scope > .header__icons');
      this.searchModal = this.header.querySelector('.header__icons details-modal.header__search');
      this.searchDetails = this.searchModal?.querySelector('details');
      this.setOpen(false);
      this.menus.forEach(menu => menu.addEventListener('toggle', () => {
        menu.querySelector('summary')?.setAttribute('aria-expanded', String(menu.open));
        if (menu.open) this.menus.filter(other => other !== menu).forEach(other => this.closeMenu(other));
        this.syncStickyLock();
      }, {signal}));
      this.toggleButton.addEventListener('click', () => {
        const open = this.dataset.open !== 'true';
        if (open && this.searchDetails?.open) {
          if (typeof this.searchModal.close === 'function') this.searchModal.close(false);
          else this.searchDetails.open = false;
        }
        this.setOpen(open);
      }, {signal});
      this.searchDetails?.addEventListener('toggle', () => {
        if (this.searchDetails.open) { this.setOpen(false); this.closeMenus(); }
      }, {signal});
      this.addEventListener('click', event => {
        if (event.target instanceof Element && event.target.closest('a[href]')) {
          this.setOpen(false); this.closeMenus();
        }
      }, {signal});
      document.addEventListener('click', event => {
        if (!this.contains(event.target) && !this.toggleButton.contains(event.target)) {
          this.setOpen(false); this.closeMenus();
        }
      }, {signal});
      document.addEventListener('keydown', event => {
        if (event.key !== 'Escape') return;
        const active = this.menus.find(menu => menu.open && menu.contains(document.activeElement));
        const openMenu = active || this.menus.find(menu => menu.open);
        if (openMenu) { event.preventDefault(); this.closeMenu(openMenu, true); return; }
        if (this.dataset.open === 'true') {
          event.preventDefault(); this.setOpen(false); this.closeMenus(); this.toggleButton.focus();
        }
      }, {signal});
      this.nav.querySelectorAll('a[href]').forEach(link => {
        const target = new URL(link.href, location.href);
        const here = new URL(location.href);
        ['preview_theme_id','_ab','_fd','_sc'].forEach(key => {target.searchParams.delete(key);here.searchParams.delete(key);});
        if (target.pathname === here.pathname && target.search === here.search) link.setAttribute('aria-current','page');
      });
      const schedule = () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.measure());
      };
      window.addEventListener('resize', schedule, {signal, passive:true});
      document.fonts?.ready.then(() => {if (this.isConnected && this.controller) schedule();});
      document.fonts?.addEventListener?.('loadingdone', schedule, {signal});
      if ('ResizeObserver' in window) {
        const observedWidths = new WeakMap();
        this.resizeObserver = new ResizeObserver(entries => {
          const changed = entries.some(entry => {
            const width = entry.contentRect.width;
            const previousWidth = observedWidths.get(entry.target) || 0;
            observedWidths.set(entry.target, width);
            return Math.abs(width - previousWidth) > 1;
          });
          if (changed) schedule();
        });
        [this.header, this.logo, this.icons].filter(Boolean).forEach(target => {
          this.resizeObserver.observe(target);
        });
      }
      if (this.section) {
        this.stateObserver = new MutationObserver(() => {
          if (this.section.classList.contains('shopify-section-header-hidden')) {this.setOpen(false);this.closeMenus();}
        });
        this.stateObserver.observe(this.section,{attributes:true,attributeFilter:['class']});
      }
      schedule();
    }
    closeMenu(menu, focus = false) {
      menu.open = false;
      const summary = menu.querySelector('summary');
      summary?.setAttribute('aria-expanded','false');
      if (focus) summary?.focus();
      this.syncStickyLock();
    }
    closeMenus() {this.menus.forEach(menu => this.closeMenu(menu));}
    setOpen(open) {
      if (open) this.syncHeaderMetrics();
      this.dataset.open = String(open);
      this.toggleButton.setAttribute('aria-expanded',String(open));
      this.syncStickyLock();
    }
    syncStickyLock() {
      if (!this.stickyHeader) return;
      this.stickyHeader.preventHide = this.dataset.open === 'true' || this.menus.some(menu => menu.open);
    }
    syncHeaderMetrics() {
      const height = Math.ceil(
        this.section?.getBoundingClientRect().height
        || this.header?.getBoundingClientRect().height
        || 0
      );
      if (height > 0) document.documentElement.style.setProperty('--header-height', `${height}px`);
      const viewportTop = Math.max(0, Math.ceil(this.header?.getBoundingClientRect().bottom || 0));
      this.style.setProperty('--ew-nav-viewport-top', `${viewportTop}px`);
    }
    measure() {
      if (!this.isConnected) return;
      const previousMode = this.header.dataset.ewNavMode;
      const activeInside = this.contains(document.activeElement) || document.activeElement === this.toggleButton;

      // Dawn switches to its mobile header grid below 990px. Never place our
      // desktop navigation into that grid or it becomes an unintended row.
      if (!window.matchMedia('(min-width: 990px)').matches) {
        this.applyMode('compact', previousMode, activeInside);
        return;
      }

      // Measure the navigation in its natural desktop layout. The desktop
      // grid uses equal outer tracks, so the wider side group determines the
      // clearance required on both sides of the centered navigation.
      this.header.dataset.ewNavMode = 'desktop';
      const logoRect = this.logo?.getBoundingClientRect();
      const iconsRect = this.icons?.getBoundingClientRect();
      const navItems = [...this.nav.children].filter(item => getComputedStyle(item).display !== 'none');
      const navStyle = getComputedStyle(this.nav);
      const headerStyle = getComputedStyle(this.header);
      const navGap = Number.parseFloat(navStyle.columnGap || navStyle.gap) || 0;
      const headerGap = Number.parseFloat(headerStyle.columnGap || headerStyle.gap) || 0;
      const navWidth = navItems.reduce(
        (total, item) => total + item.getBoundingClientRect().width,
        0
      ) + Math.max(0, navItems.length - 1) * navGap;
      const availableWidth = this.header.clientWidth
        - (Number.parseFloat(headerStyle.paddingLeft) || 0)
        - (Number.parseFloat(headerStyle.paddingRight) || 0);
      const sideWidth = Math.max(logoRect?.width || 0, iconsRect?.width || 0);
      const collisionBuffer = 16;
      const requiredWidth = navWidth
        + sideWidth * 2
        + headerGap * 2
        + collisionBuffer;
      const nextMode = requiredWidth <= availableWidth ? 'desktop' : 'compact';

      this.applyMode(nextMode, previousMode, activeInside);
    }
    applyMode(nextMode, previousMode, activeInside) {
      this.header.dataset.ewNavMode = nextMode;
      if (previousMode && previousMode !== nextMode) {
        this.setOpen(false);
        this.closeMenus();
        if (activeInside) {
          const focusTarget = nextMode === 'compact'
            ? this.toggleButton
            : this.nav.querySelector('a[href], summary');
          window.requestAnimationFrame(() => {
            if (this.isConnected) focusTarget?.focus({ preventScroll: true });
          });
        }
      } else if (nextMode === 'desktop') {
        this.setOpen(false);
      }
      this.syncHeaderMetrics();
    }
    disconnectedCallback() {
      if (this.stickyHeader) this.stickyHeader.preventHide = false;
      if (this.toggleButton?.isConnected) this.toggleButton.hidden = true;
      this.controller?.abort();this.controller=null;
      this.resizeObserver?.disconnect();this.stateObserver?.disconnect();cancelAnimationFrame(this.frame);
      if (this.header && !this.header.querySelector('ew-navigation')) delete this.header.dataset.ewNavMode;
    }
  }
  customElements.define('ew-navigation',EWNavigation);
})();
